import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  TextField,
  Button,
  CircularProgress,
  LinearProgress,
  Alert,
  Divider,
  Tooltip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import apiService from "../../services/api";
import { createLogger, adminLogContext } from '../../utils/logger'

const logger = createLogger('SourceConfig')
const ctx = (extra) => adminLogContext('source_configuration', extra)


const SourceConfig = () => {
  const [counties, setCounties] = useState([]);
  const [selectedCountyId, setSelectedCountyId] = useState("");
  const [scrapingStatus, setScrapingStatus] = useState({}); // { county_id: boolean }

  const [regions, setRegions] = useState([]);
  const [selectedRegionId, setSelectedRegionId] = useState(null);

  const [form, setForm] = useState({
    scraping_data_id: null,
    county_id: "",
    municipality_geoid: null,
    type: "",
    name: "",
    legistar_client: "",
    civicclerk_site_id: "",
  });

  const [loadingCounties, setLoadingCounties] = useState(false);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const fetchCountiesAndStatus = async () => {
      try {
        setLoadingCounties(true);
        setError("");
        
        // Fetch counties and scraping status in parallel
        const [countiesResp, statusResp] = await Promise.all([
          apiService.getAllCounties(),
          apiService.getCountiesScrapingStatus(),
        ]);
        
        setCounties(countiesResp.counties || []);
        setScrapingStatus(statusResp.status || {});
      } catch (err) {
        logger.apiFailure('Failed to load counties', ctx({ action: 'load_counties' }), err);
        setError("Failed to load counties.");
      } finally {
        setLoadingCounties(false);
      }
    };
    fetchCountiesAndStatus();
  }, []);

  const handleCountyChange = async (event) => {
    const countyId = event.target.value;
    setSelectedCountyId(countyId);
    setRegions([]);
    setSelectedRegionId(null);
    setSuccessMessage("");
    setError("");

    if (!countyId) return;

    try {
      setLoadingRegions(true);
      const data = await apiService.getScrapingRegions(countyId);
      setRegions(data || []);
    } catch (err) {
      logger.apiFailure('Failed to load scraping regions', ctx({
        action: 'load_scraping_regions',
        county_id: String(countyId),
      }), err);
      setError(
        err?.message || "Failed to load scraping regions for this county.",
      );
    } finally {
      setLoadingRegions(false);
    }
  };

  const selectRegion = (region) => {
    setSelectedRegionId(
      region.municipality_geoid || `unincorporated-${region.county_id}`,
    );
    setForm({
      scraping_data_id: region.scraping_data_id || null,
      county_id: region.county_id,
      municipality_geoid: region.municipality_geoid || null,
      type: region.type,
      name: region.name || "",
      legistar_client: region.legistar_client || "",
      civicclerk_site_id: region.civicclerk_site_id || "",
    });
    setSuccessMessage("");
    setError("");
  };

  const handleFieldChange = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSave = async () => {
    if (!form.county_id || !form.name || !form.type) {
      setError("Region name and type are required.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccessMessage("");

      const payload = {
        scraping_data_id: form.scraping_data_id,
        county_id: form.county_id,
        municipality_geoid: form.municipality_geoid,
        name: form.name,
        type: form.type,
        legistar_client: form.legistar_client || null,
        civicclerk_site_id: form.civicclerk_site_id || null,
      };

      const saved = await apiService.saveScrapingConfig(payload);

      // Refresh regions so the left list reflects new status / IDs
      const updatedRegions = await apiService.getScrapingRegions(
        form.county_id,
      );
      setRegions(updatedRegions || []);

      setForm((prev) => ({
        ...prev,
        scraping_data_id: saved.id,
      }));

      // Update the scraping status for this county based on saved config
      const hasConfig = !!(
        payload.legistar_client ||
        payload.civicclerk_site_id
      );
      if (hasConfig) {
        setScrapingStatus((prev) => ({
          ...prev,
          [form.county_id]: true,
        }));
      }

      setSuccessMessage("Scraping configuration saved.");
    } catch (err) {
      logger.apiFailure('Failed to save scraping config', ctx({
        action: 'save_scraping_config',
        county_id: String(form.county_id),
        region_name: form.name,
      }), err);
      setError(err?.message || "Failed to save scraping configuration.");
    } finally {
      setSaving(false);
    }
  };

  const selectedRegion =
    regions.find(
      (r) =>
        (r.municipality_geoid || `unincorporated-${r.county_id}`) ===
        selectedRegionId,
    ) || null;

  const renderSourceChips = (region) => {
    const chips = [];
    if (region.legistar_client)
      chips.push(
        <Chip key="legistar" label="Legistar" size="small" sx={{ mr: 0.5 }} />,
      );
    if (region.civicclerk_site_id)
      chips.push(
        <Chip
          key="civicclerk"
          label="CivicClerk"
          size="small"
          sx={{ mr: 0.5 }}
        />,
      );
    if (!chips.length)
      chips.push(
        <Chip
          key="none"
          label="Not configured"
          size="small"
          variant="outlined"
          sx={{ mr: 0.5 }}
        />,
      );
    return chips;
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h5" gutterBottom>
        Source Configuration
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Select a county, then choose a region (unincorporated area or
        municipality) to configure Legistar or CivicClerk scraper sources.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel id="county-select-label">County</InputLabel>
          <Select
            labelId="county-select-label"
            label="County"
            value={selectedCountyId}
            onChange={handleCountyChange}
            MenuProps={{
              disablePortal: true,
              PaperProps: {
                sx: { zIndex: 9999 },
              },
            }}
          >
            {loadingCounties && (
              <MenuItem value="">
                <CircularProgress size={18} sx={{ mr: 1 }} /> Loading...
              </MenuItem>
            )}
            {!loadingCounties &&
              counties.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {scrapingStatus[c.id] ? (
                      <Tooltip title="Sources configured">
                        <CheckCircleIcon sx={{ fontSize: 18, color: "success.main" }} />
                      </Tooltip>
                    ) : (
                      <Tooltip title="No sources configured">
                        <CancelIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                      </Tooltip>
                    )}
                    {c.name}
                  </Box>
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Paper>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Paper sx={{ flex: 1, p: 2, minHeight: 300 }}>
          <Typography variant="subtitle1" gutterBottom>
            Regions in County
          </Typography>
          {loadingRegions && <LinearProgress sx={{ mb: 1 }} />}
          <List sx={{ maxHeight: 400, overflow: "auto", opacity: loadingRegions ? 0.5 : 1, transition: 'opacity 0.2s' }}>
              {regions.map((region) => {
                const key =
                  region.municipality_geoid ||
                  `unincorporated-${region.county_id}`;
                const selected = key === selectedRegionId;
                return (
                  <ListItemButton
                    key={key}
                    selected={selected}
                    onClick={() => selectRegion(region)}
                  >
                    <ListItemText
                      primary={region.name}
                      secondary={
                        <Box sx={{ mt: 0.5 }}>{renderSourceChips(region)}</Box>
                      }
                    />
                  </ListItemButton>
                );
              })}
              {!regions.length && (
                <Typography variant="body2" color="text.secondary">
                  Select a county to see its regions.
                </Typography>
              )}
            </List>
        </Paper>

        <Paper sx={{ flex: 2, p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Source settings
          </Typography>
          {!selectedRegion ? (
            <Typography variant="body2" color="text.secondary">
              Select a region on the left to edit its scraper configuration.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Configuring: <strong>{selectedRegion.name}</strong>
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  label="Region Display Name"
                  size="small"
                  value={form.name}
                  onChange={handleFieldChange("name")}
                />
                <TextField
                  label="Legistar client"
                  size="small"
                  value={form.legistar_client}
                  onChange={handleFieldChange("legistar_client")}
                  helperText="e.g., 'mecklenburg'"
                />
                <TextField
                  label="CivicClerk site id"
                  size="small"
                  value={form.civicclerk_site_id}
                  onChange={handleFieldChange("civicclerk_site_id")}
                  helperText="e.g., 'huntersvillenc'"
                />
                <Box sx={{ mt: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? "Saving…" : "Save configuration"}
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default SourceConfig;
